import { useEffect } from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import { Box, Breadcrumbs, Link as MuiLink, Typography } from '@mui/material'
import { Home as HomeIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useUserStore } from '@/stores/userStore'
import { ROUTES } from '@/utils/constants'
import { ActivityPage } from './ActivityPage'
import '@/styles/dashboard.css'

/**
 * Активность конкретного ученика — пункт левого меню в контексте ученика
 * (/users/:id/activity). Доступ проверяет API (учитель — своего ученика).
 */
export function UserActivityPage() {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation()
  const userId = id ? parseInt(id, 10) : NaN
  const currentUser = useUserStore((state) => state.currentUser)
  const fetchUser = useUserStore((state) => state.fetchUser)

  useEffect(() => {
    if (Number.isNaN(userId)) return
    fetchUser(userId).catch(() => {})
  }, [userId, fetchUser])

  const isLoaded = currentUser !== null && currentUser.id === userId

  const userName = isLoaded
    ? [currentUser.surname, currentUser.first_name].filter(Boolean).join(' ').trim() ||
      currentUser.email
    : undefined

  // На этом маршруте субъект — всегда ученик учителя; роли из API, если пришли
  const subjectRoles = isLoaded && currentUser.roles?.length
    ? currentUser.roles.map((role) => role.slug)
    : ['student']

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 }, pb: 0 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
          <MuiLink
            component={RouterLink}
            to={ROUTES.DASHBOARD}
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </MuiLink>
          <MuiLink component={RouterLink} to={ROUTES.USERS} underline="hover">
            {t('users.title')}
          </MuiLink>
          {userName && (
            <MuiLink
              component={RouterLink}
              to={`${ROUTES.USERS}/${userId}`}
              underline="hover"
            >
              {userName}
            </MuiLink>
          )}
          <Typography color="text.primary">{t('activity.title')}</Typography>
        </Breadcrumbs>
      </Box>

      <ActivityPage
        userId={Number.isNaN(userId) ? undefined : userId}
        userName={userName}
        subjectRoles={subjectRoles}
      />
    </DashboardLayout>
  )
}
